import {
  formatCodeableConcept,
  formatObservationValue as formatMedplumObservationValue,
  getExtension,
  isResource,
  getDisplayString as medplumGetDisplayString,
  type SearchRequest,
} from '@medplum/core'
import type {
  Observation,
  Quantity,
  QuestionnaireResponse,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { format } from 'date-fns'
import { compact } from 'lodash-es'
import {
  displayFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import { ORDER_AI_TITLE_SYSTEM } from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
// oxlint-disable-next-line no-restricted-imports
import {
  GOAL_CARE_PLAN_CATEGORY,
  INTERVENTION_CARE_PLAN_CATEGORY,
} from '#src/models/care-plans/constants.ts'
// oxlint-disable-next-line no-restricted-imports
import * as questionnaires from '#src/models/questionnaires/index.ts'
import { getReferenceString } from '#src/references.ts'
import {
  getNickname,
  humanNameToString,
  isOfType,
  officialNameOrFallback,
} from '#src/utils.ts'

/* The questionnaire a response answered, which is what names the response
 * wherever one is shown. Exported so a caller that supplies its own date — an
 * AI proposal's source chip, which prints the date the response was signed —
 * gets the same name as `getDisplayString` without also getting its date. */
export function questionnaireResponseTitle(
  resource: QuestionnaireResponse
): string | undefined {
  return questionnaires.findByUrl(resource.questionnaire)?.title
}

interface ClinicalUnitLabels {
  readonly [unit: string]: string
}

const CLINICAL_UNIT_LABELS: ClinicalUnitLabels = {
  '[degC]': '°C',
  '[degF]': '°F',
  '[in_us]': 'in',
  '[lb_av]': 'lbs',
  '{beats}/min': 'bpm',
  '{breaths}/min': 'breaths/min',
  'kg/m2': 'kg/m²',
  'mL/min/{1.73_m2}': 'mL/min/1.73m²',
}

export function formatClinicalUnit(unit: string): string {
  return CLINICAL_UNIT_LABELS[unit] ?? unit
}

function formatQuantityValue(quantity: Quantity): string {
  return `${quantity.comparator ? `${quantity.comparator} ` : ''}${quantity.value}`
}

function quantityUnit(quantity: Quantity): string | undefined {
  return quantity.unit || quantity.code || undefined
}

export function formatQuantityDisplay(quantity: Quantity): string {
  if (quantity.value === undefined) {
    return ''
  }

  const value = formatQuantityValue(quantity)
  const unit = quantityUnit(quantity)
  if (!unit) {
    return value
  }

  const unitLabel = formatClinicalUnit(unit)
  return `${value}${unitLabel === '%' ? '' : ' '}${unitLabel}`
}

export function formatObservationValueDisplay(
  observation: Observation
): string {
  if (observation.valueQuantity) {
    return formatQuantityDisplay(observation.valueQuantity)
  }

  const componentQuantities = observation.component?.map(
    (component) => component.valueQuantity
  )
  if (
    componentQuantities?.length &&
    componentQuantities.every(
      (quantity): quantity is Quantity => quantity?.value !== undefined
    )
  ) {
    const unitLabels = componentQuantities.map((quantity) => {
      const unit = quantityUnit(quantity)
      return unit ? formatClinicalUnit(unit) : ''
    })
    const sharedUnit = unitLabels.every((unit) => unit === unitLabels[0])
      ? unitLabels[0]
      : undefined

    if (sharedUnit) {
      return `${componentQuantities.map(formatQuantityValue).join('/')}${
        sharedUnit === '%' ? '' : ' '
      }${sharedUnit}`
    }

    return componentQuantities.map(formatQuantityDisplay).join(' / ')
  }

  return formatMedplumObservationValue(observation)
}

// Replaces a rendered label, e.g. to hide participant names in demo mode. It
// sees the label and the Reference it came from.
export type ReferenceDisplayTransform = (
  label: string,
  reference: Reference
) => string

let referenceDisplayTransform: ReferenceDisplayTransform | undefined

export function setReferenceDisplayTransform(
  transform: ReferenceDisplayTransform | undefined
): void {
  referenceDisplayTransform = transform
}

// The label to render for a Reference. Render through this, never
// `reference.display`; to render a Resource, pass `createReference(resource)`.
// `getDisplayString()` and `createReference()` stay untransformed because
// their output is written to FHIR.
export function referenceDisplay(reference: { display: string }): string
export function referenceDisplay(
  reference: Reference | undefined
): string | undefined
export function referenceDisplay(
  reference: Reference | undefined
): string | undefined {
  const label = reference?.display
  if (!reference || label === undefined || !referenceDisplayTransform) {
    return label
  }
  return referenceDisplayTransform(label, reference)
}

export function getDisplayString(resource?: Resource): string {
  if (!resource) {
    return ''
  }
  if (resource.resourceType === 'ClinicalImpression' && resource.description) {
    return resource.description
  }
  if (resource.resourceType === 'Composition' && resource.title) {
    return resource.title
  }
  if (
    resource.resourceType === 'MedicationRequest' &&
    resource.medicationReference?.display
  ) {
    return resource.medicationReference.display
  }
  if (resource.resourceType === 'Library') {
    return resource.title ?? resource.name ?? 'Library'
  }
  if (resource.resourceType === 'Appointment' && resource.start) {
    const start = format(resource.start, 'MM/dd/yy')
    const healthcareService = resource.participant.find((p) =>
      isOfType(p.actor, 'HealthcareService')
    )?.actor?.display
    const practitioner = resource.participant.find((p) =>
      isOfType(p.actor, 'Practitioner')
    )?.actor?.display
    return (
      `${start} ${healthcareService ?? 'Appointment'} with ${practitioner}` +
      (resource.status ? ` (${resource.status})` : '')
    )
  }
  if (resource.resourceType === 'HealthcareService') {
    const category = displayFromCodeableConcept(resource.category)
    const type = displayFromCodeableConcept(resource.type)
    return resource.name ?? compact([category, type]).join(' - ')
  }
  if (resource.resourceType === 'Task') {
    // Check for SDR before checking for Task.
    if ('title' in resource) {
      // TODO(yang): checking instanceof against SDR will create a circular dependency
      return resource.title as string
    }
    if (resource.description) {
      return resource.description
    }
  }
  if (resource.resourceType === 'Composition' && resource.title) {
    return resource.title
  }
  if (resource.resourceType === 'Patient') {
    const humanName = officialNameOrFallback(resource)
    const nickname = getNickname(resource)
    return humanName
      ? `${humanNameToString(humanName)}${nickname ? ` (${nickname})` : ''}`
      : 'Participant'
  }
  if (resource.resourceType === 'CarePlan') {
    // Goals, interventions, and initial care plans are all CarePlans,
    // distinguished by their CarePlanCategory. Only interventions carry their
    // text in `description` (no title); goal titles are authored as HTML.
    if (
      hasCodeableConcept(resource.category, INTERVENTION_CARE_PLAN_CATEGORY)
    ) {
      return resource.title ?? resource.description ?? 'Intervention'
    }
    if (hasCodeableConcept(resource.category, GOAL_CARE_PLAN_CATEGORY)) {
      return resource.title ?? 'Goal'
    }
    return resource.title ?? 'Care Plan'
  }
  if (resource.resourceType === 'Location') {
    return resource.name ?? resource.alias?.[0] ?? 'Location'
  }
  if (resource.resourceType === 'QuestionnaireResponse') {
    const title = questionnaireResponseTitle(resource)
    if (title) {
      return `${title}${resource.authored ? ` on ${format(new Date(resource.authored), 'M/d/yy')}` : ''}`
    }
  }
  if (resource.resourceType === 'Coverage') {
    return (
      formatCodeableConcept(resource.type) ?? medplumGetDisplayString(resource)
    )
  }
  if (resource.resourceType === 'Observation') {
    const codeDisplay =
      displayFromCodeableConcept(resource.code) ??
      resource.code?.text ??
      'Observation'
    let valueString =
      displayFromCodeableConcept(resource.valueCodeableConcept) ??
      resource.valueCodeableConcept?.text ??
      resource.valueString
    if (!valueString && resource.valueQuantity) {
      valueString = compact([
        resource.valueQuantity.value,
        resource.valueQuantity.unit,
      ]).join('')
    }
    if (!valueString && resource.component?.length) {
      valueString = resource.component
        .map((c) =>
          compact([displayFromCodeableConcept(c.code), c.valueString]).join('|')
        )
        .join(', ')
    }
    return valueString ? `${codeDisplay}: ${valueString}` : codeDisplay
  }
  if (resource.resourceType === 'Condition') {
    if (resource.text?.div) {
      return resource.text.div
    }
    if (resource.code?.text) {
      return resource.code.text
    }
  }
  if (resource.resourceType === 'DocumentReference') {
    return (
      resource.description ??
      resource.content?.[0]?.attachment?.title ??
      'Document'
    )
  }
  if (resource.resourceType === 'Medication') {
    return (
      displayFromCodeableConcept(resource.code) ??
      resource.text?.div ??
      `Medication/${resource.id}`
    )
  }
  if (resource.resourceType === 'ServiceRequest') {
    // Match order list precedence (aiTitle first, then the human-typed
    // narrative title); the orderDetail description is often HTML boilerplate.
    return (
      getExtension(resource, ORDER_AI_TITLE_SYSTEM)?.valueString ??
      resource.text?.div ??
      resource.orderDetail?.find((detail) => detail.text)?.text ??
      medplumGetDisplayString(resource)
    )
  }
  return medplumGetDisplayString(resource)
}

export function getDetailString(resource: Resource): string {
  if (resource.resourceType === 'Patient' && resource.birthDate) {
    return resource.birthDate
  }
  if (resource.resourceType === 'Practitioner') {
    return ''
  }
  return format(getResourceDate(resource), "M/d/yy 'at' h:mm a")
}

export function getResourceDate(resource: Resource): string {
  invariant(resource.meta?.lastUpdated, 'Resource must have a lastUpdated date')
  if (
    (resource.resourceType === 'ClinicalImpression' ||
      resource.resourceType === 'Observation') &&
    resource.effectiveDateTime
  ) {
    return resource.effectiveDateTime
  }
  if (resource.resourceType === 'Appointment' && resource.start) {
    return resource.start
  }
  if (resource.resourceType === 'MedicationRequest' && resource.authoredOn) {
    return resource.authoredOn
  }
  if (resource.resourceType === 'QuestionnaireResponse' && resource.authored) {
    return resource.authored
  }
  return resource.meta.lastUpdated
}

export function getResourceOwnerSortFilterValue(
  resource: Resource
): string | undefined {
  switch (resource.resourceType) {
    case 'Appointment':
      return resource.participant?.[0]?.actor?.display
    case 'MedicationRequest':
      return resource.requester?.display
    case 'ClinicalImpression':
      return resource.assessor?.display
    case 'Composition':
      return resource.author?.[0]?.display
    case 'Task':
      return resource.owner?.display
    default:
      throw new Error(
        `Invalid sort filter value ${getReferenceString(resource)}`
      )
  }
}

export interface SearchRequestObject {
  _count?: string
  _offset?: string
  _fields?: string
  _sort?: string
  _total?: 'accurate' | 'estimate' | 'none'
  [key: string]: string | undefined
}

export function searchRequestAsObject(
  searchRequest: SearchRequest
): SearchRequestObject {
  const result: SearchRequestObject = {}

  if (searchRequest.count !== undefined) {
    result._count = searchRequest.count.toString()
  }

  if (searchRequest.offset !== undefined) {
    result._offset = searchRequest.offset.toString()
  }

  if (searchRequest.fields && searchRequest.fields.length > 0) {
    result._fields = searchRequest.fields.join(',')
  }

  if (searchRequest.sortRules && searchRequest.sortRules.length > 0) {
    result._sort = searchRequest.sortRules
      .map((rule) => (rule.descending ? `-${rule.code}` : rule.code))
      .join(',')
  }

  if (searchRequest.total) {
    result._total = searchRequest.total
  }

  return result
}

export const DEFAULT_SEARCH_COUNT = 10

export type ResourceToCreate<T extends Resource> = {
  resource: T
  fullUrl?: string
}
export function createReferenceFromResourceToCreate<T extends Resource>(
  inputResource: ResourceToCreate<T> | T
): Reference<T> {
  let referenceText: string
  let displayText: string
  if (isResource(inputResource)) {
    referenceText = getReferenceString(inputResource)
    displayText = getDisplayString(inputResource)
  } else {
    referenceText =
      inputResource.fullUrl ?? getReferenceString(inputResource.resource)
    displayText = getDisplayString(inputResource.resource)
  }

  return referenceText === displayText
    ? {
        reference: referenceText,
      }
    : {
        reference: referenceText,
        display: displayText,
      }
}
